import Fuse, { type IFuseOptions } from 'fuse.js'

/**
 * Server-side problem search engine (LG-448).
 *
 * Fuzzy matching keeps the exact engine and configuration the admin browser
 * uses today (use-problem-search.ts / use-table-filters.ts) — both
 * database-native alternatives (ILIKE, pg_trgm) were prototyped against the
 * real corpus and rejected on behaviour. Search parity depends on the
 * configuration below being preserved exactly.
 *
 * This module is pure: rows and the corpus version arrive through injected
 * loaders, so rebuild-versus-reuse is deterministic in tests and nothing here
 * touches the database client.
 */

/** One searchable source attribution on a problem. */
export interface ProblemSearchSourceRef {
  title: string | null
  source: string | null
  externalId: string | null
}

/** The lightweight row the index holds and search returns. */
export interface ProblemSearchRow {
  id: string
  title: string
  slug: string | null
  externalId: string | null
  sources: ProblemSearchSourceRef[]
}

/** Below this length, non-numeric queries return nothing (Fuse minimum). */
export const PROBLEM_SEARCH_MIN_QUERY_LENGTH = 2

// Carried over verbatim from the client hooks — weights, threshold, and
// minMatchCharLength are the decision, not a starting point.
const fuseOptions: IFuseOptions<ProblemSearchRow> = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'externalId', weight: 0.15 },
    { name: 'sources.title', weight: 0.25 },
    { name: 'sources.source', weight: 0.1 },
    { name: 'sources.externalId', weight: 0.1 },
  ],
  threshold: 0.4,
  minMatchCharLength: PROBLEM_SEARCH_MIN_QUERY_LENGTH,
  includeScore: true,
  shouldSort: true,
}

/**
 * Search prepared rows through a prebuilt Fuse index.
 *
 * A purely numeric query is an exact external-id lookup on the problem and
 * its sources — fuzzy matching would drown "146" in every "1462"-style id —
 * returned in corpus order. Everything else is fuzzy, in relevance order.
 */
function searchPrepared(
  rows: ProblemSearchRow[],
  fuse: Fuse<ProblemSearchRow>,
  query: string,
  limit: number,
): ProblemSearchRow[] {
  const trimmed = query.trim()
  if (/^\d+$/.test(trimmed)) {
    return rows
      .filter(
        (row) =>
          row.externalId === trimmed ||
          row.sources.some((s) => s.externalId === trimmed),
      )
      .slice(0, limit)
  }
  if (trimmed.length < PROBLEM_SEARCH_MIN_QUERY_LENGTH) {
    return []
  }
  return fuse.search(trimmed, { limit }).map((result) => result.item)
}

/** One-shot search over unindexed rows. Builds a throwaway Fuse index. */
export function searchProblemRows(
  rows: ProblemSearchRow[],
  query: string,
  limit: number,
): ProblemSearchRow[] {
  return searchPrepared(rows, new Fuse(rows, fuseOptions), query, limit)
}

/**
 * Where the index's rows and freshness signal come from.
 *
 * `loadVersion` is the corpus version probe: an opaque string that moves
 * whenever the searchable corpus changes (row count + newest change
 * timestamp). Probing before every search — rather than expiring on a timer —
 * is deliberate: the API function is serverless, so a mutation handled by one
 * warm instance cannot invalidate a cache held by another, and any timer
 * would leave a window in which a newly created problem is unfindable.
 */
export interface ProblemSearchIndexSource {
  loadVersion(): Promise<string>
  loadRows(): Promise<ProblemSearchRow[]>
}

interface BuiltIndex {
  version: string
  rows: ProblemSearchRow[]
  fuse: Fuse<ProblemSearchRow>
}

/**
 * A process-cached search index validated against the version probe before
 * each use and rebuilt only when the probe has moved. Loading rows is the
 * expensive step (~536ms on the dev corpus, versus ~7ms to index and ~1ms to
 * probe), so concurrent cold searches share a single in-flight load.
 */
export function createProblemSearchIndex(source: ProblemSearchIndexSource) {
  let cached: BuiltIndex | null = null
  let building: Promise<BuiltIndex> | null = null

  async function ensureFresh(): Promise<BuiltIndex> {
    // A caller can join a shared in-flight build that was probed before a
    // write landed, so it goes around once more for a fresh build. Once,
    // not until versions match: a build's rows are read after the version
    // it is stamped with, so one revalidation already bounds staleness to
    // a single build window — an unbounded loop would let a sustained
    // write stream livelock every search in probe/rebuild cycles.
    for (let revalidations = 0; ; revalidations++) {
      const version = await source.loadVersion()
      if (cached && cached.version === version) {
        return cached
      }
      if (!building) {
        building = source
          .loadRows()
          .then((rows) => {
            cached = { version, rows, fuse: new Fuse(rows, fuseOptions) }
            return cached
          })
          .finally(() => {
            building = null
          })
      }
      const built = await building
      if (built.version === version || revalidations >= 1) {
        return built
      }
    }
  }

  return {
    async search(query: string, limit: number): Promise<ProblemSearchRow[]> {
      const index = await ensureFresh()
      return searchPrepared(index.rows, index.fuse, query, limit)
    },
  }
}
